import { isSupportedLocale, type SupportedLocale } from "./locales"

/**
 * Client-readable mirror of the viewer's resolved UI locale, read server-side by
 * `request.ts` and written browser-side by `LocaleSync` / the language picker.
 *
 * Deliberately not `httpOnly`: the browser writes it, which is what keeps the
 * locale off the request critical path. Tampering only changes the tamperer's
 * own UI language, and every read validates against `SUPPORTED_LOCALES`, so the
 * value is never trusted beyond "which message bundle to load".
 */
export const LOCALE_COOKIE = "siro-locale"

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

export const parseLocaleCookie = (
  value: string | undefined | null,
): SupportedLocale | null => (isSupportedLocale(value) ? value : null)

/** Browser-only — server readers go through `cookies()` + `parseLocaleCookie`. */
export const readLocaleCookie = (): SupportedLocale | null =>
  parseLocaleCookie(
    document.cookie
      .split("; ")
      .find((entry) => entry.startsWith(`${LOCALE_COOKIE}=`))
      ?.slice(LOCALE_COOKIE.length + 1),
  )

/** Browser-only. */
export const writeLocaleCookie = (locale: SupportedLocale): void => {
  // biome-ignore lint/suspicious/noDocumentCookie: the suggested Cookie Store API is unsupported in Safari and Firefox; this cookie must be set on every browser or request.ts falls back to the profile fetch it exists to avoid.
  document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=${ONE_YEAR_SECONDS}; samesite=lax`
}
